import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import AuthLayout from '@shared/layouts/AuthLayout';
import { Card } from '@shared/components/ui/Card';
import { Badge } from '@shared/components/ui/Badge';
import { Button } from '@shared/components/ui/Button';
import * as Icons from 'lucide-react';
import { planApi } from '@features/billing/api/planApi';
import type { Plan } from '@features/billing/types/plan';

const Pricing = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('billing');
  const locale =
    i18n.resolvedLanguage === 'en' ? 'en-US' : i18n.resolvedLanguage === 'es' ? 'es-ES' : 'pt-BR';
  const formatPlanPrice = (cents: number, currency = 'BRL') =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  const planLabel = (plan: Plan) =>
    t(`pricing.planNames.${plan.name}`, { defaultValue: plan.displayName || plan.name });
  const planDescription = (plan: Plan) =>
    t(`pricing.planDescriptions.${plan.name}`, { defaultValue: plan.description || '' });
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const publicPlans = await planApi.getPublicPlans();
        const activePlans = publicPlans
          .filter((p) => p.isActive && p.isPublic)
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setPlans(activePlans);
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [retryAttempt]);

  const handleGetStarted = (planName: string) => {
    navigate(`/checkout?plan=${encodeURIComponent(planName)}`);
  };

  // Helper to format max agents
  const formatMaxAgents = (plan: Plan) => {
    if (plan.hasUnlimitedAgents) return t('pricing.unlimited');
    return plan.maxAgents || plan.agentsIncluded;
  };

  // Helper to format extra agent price
  const formatExtraAgentPrice = (plan: Plan) => {
    if (!plan.extraAgentPrice) return '—';
    return `+ ${formatPlanPrice(plan.extraAgentPrice, plan.currency)}`;
  };

  // Helper to format bonus storage per agent
  const formatBonusStorage = (plan: Plan) => {
    if (!plan.extraAgentStorageGB) return '—';
    return `+ ${plan.extraAgentStorageGB} GB`;
  };

  // Helper to format extra storage price
  const formatExtraStorage = (plan: Plan) => {
    if (!plan.hasExtraStorage) return '—';
    return `${formatPlanPrice(plan.extraStoragePrice, plan.currency)} / ${plan.extraStorageAmountGB} GB`;
  };

  // Helper to get retention days from features
  const getRetentionDays = (plan: Plan) => {
    const retention = plan.features?.retentionDays;
    if (!retention || retention === 0) return t('pricing.unlimited');
    return t('pricing.days', { count: retention });
  };

  // Helper to get support level from features
  const getSupportLevel = (plan: Plan) => {
    const support = plan.features?.support;
    if (support === 'phone') return t('pricing.phoneSupport');
    if (support === 'priority') return t('pricing.priority');
    return t('pricing.email');
  };

  // Helper to get scheduling from features
  const getScheduling = (plan: Plan) => {
    const minInterval = plan.features?.minScheduleInterval;
    if (!minInterval || minInterval === 0) return t('pricing.unlimited');
    if (minInterval >= 60) return t('pricing.hourly');
    if (minInterval >= 15) return t('pricing.every15');
    if (minInterval >= 5) return t('pricing.every5');
    return t('pricing.every1');
  };

  if (loading) {
    return (
      <AuthLayout maxWidth="">
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </AuthLayout>
    );
  }

  // Separate trial and paid plans
  const trialPlan = plans.find((p) => p.name === 'Trial');
  const paidPlans = plans.filter((p) => p.name !== 'Trial');
  const displayPlans = trialPlan ? [trialPlan, ...paidPlans] : paidPlans;

  return (
    <AuthLayout maxWidth="">
      <div className="py-12 px-4">
        <div className="text-center mb-16">
          <Badge variant="info" className="mb-4">
            {t('pricing.badge')}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-6 tracking-tight uppercase">
            {t('pricing.heroTitle')}
          </h1>
          <p className="text-lg text-text-muted-light dark:text-text-muted-dark max-w-2xl mx-auto font-medium">
            {t('pricing.subtitle')}
          </p>
        </div>

        {loadError ? (
          <div
            className="mx-auto max-w-xl rounded-xl border border-state-danger-light/30 bg-state-danger-bg-light p-6 text-center text-state-danger-light dark:border-state-danger-dark/30 dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
            role="alert"
          >
            <p className="font-semibold">{t('pricing.errors.load')}</p>
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => setRetryAttempt((attempt) => attempt + 1)}
            >
              {t('pricing.errors.retry')}
            </Button>
          </div>
        ) : displayPlans.length === 0 ? (
          <div className="mx-auto max-w-xl rounded-xl border border-surface-border-light bg-surface-card-light p-6 text-center dark:border-surface-border-dark dark:bg-surface-card-dark">
            <p className="font-semibold text-text-primary-light dark:text-text-primary-dark">
              {t('pricing.emptyTitle')}
            </p>
            <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark">
              {t('pricing.emptyDescription')}
            </p>
          </div>
        ) : null}

        {/* Plan Cards - Creative Bento Layout */}
        <div className="max-w-7xl mx-auto mb-24">
          {!loadError && displayPlans.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-6">
              {/* Trial - Compact top left */}
              {displayPlans[0] && (
                <Card className="lg:col-span-3 p-5 flex flex-col bg-surface-2-light dark:bg-surface-2-dark border-surface-border-light dark:border-surface-border-dark hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-black text-slate-900 dark:text-white">
                      {planLabel(displayPlans[0])}
                    </h3>
                    <Icons.Zap className="w-5 h-5 text-state-warn-light dark:text-state-warn-dark" />
                  </div>
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark mb-3">
                    {planDescription(displayPlans[0])}
                  </p>
                  <div className="mb-4">
                    <span className="text-3xl font-black text-slate-900 dark:text-white">
                      {t('pricing.free')}
                    </span>
                  </div>
                  <ul className="space-y-2 mb-4 flex-grow text-xs">
                    <li className="flex items-center text-text-muted-light dark:text-text-muted-dark">
                      <Icons.HardDrive className="w-3.5 h-3.5 mr-2 text-brand-600 dark:text-brand-400 " />
                      {t('pricing.storageAmount', { amount: displayPlans[0].storageIncludedGB })}
                    </li>
                    <li className="flex items-center text-text-muted-light dark:text-text-muted-dark">
                      <Icons.Cpu className="w-3.5 h-3.5 mr-2 text-brand-600 dark:text-brand-400 " />
                      {t('pricing.agents', { count: displayPlans[0].agentsIncluded })}
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleGetStarted(displayPlans[0].name)}
                    variant="outline"
                    className="w-full"
                  >
                    {t('pricing.startTrial')}
                  </Button>
                </Card>
              )}

              {/* Starter & Business - Middle row */}
              {displayPlans[1] && (
                <Card className="lg:col-span-3 p-5 flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-surface-border-light dark:border-surface-border-dark">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-black text-slate-900 dark:text-white">
                      {planLabel(displayPlans[1])}
                    </h3>
                    <Icons.Rocket className="w-5 h-5 text-brand-600 dark:text-brand-400 " />
                  </div>
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark mb-3">
                    {planDescription(displayPlans[1])}
                  </p>
                  <div className="mb-4">
                    <span className="text-3xl font-black text-slate-900 dark:text-white">
                      {formatPlanPrice(displayPlans[1].monthlyPrice, displayPlans[1].currency)}
                    </span>
                    <span className="text-text-subtle-light dark:text-text-subtle-dark text-sm">
                      {t('pricing.perMonth')}
                    </span>
                  </div>
                  <ul className="space-y-2 mb-4 flex-grow text-xs">
                    <li className="flex items-center text-text-muted-light dark:text-text-muted-dark">
                      <Icons.HardDrive className="w-3.5 h-3.5 mr-2 text-brand-600 dark:text-brand-400 " />
                      {t('pricing.storageAmount', { amount: displayPlans[1].storageIncludedGB })}
                    </li>
                    <li className="flex items-center text-text-muted-light dark:text-text-muted-dark">
                      <Icons.Cpu className="w-3.5 h-3.5 mr-2 text-brand-600 dark:text-brand-400 " />
                      {t('pricing.agents', { count: displayPlans[1].agentsIncluded })}
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleGetStarted(displayPlans[1].name)}
                    variant="outline"
                    className="w-full"
                  >
                    {t('pricing.buyNow')}
                  </Button>
                </Card>
              )}

              {/* Professional - HERO CARD (Featured) */}
              {displayPlans[2] && (
                <Card className="lg:col-span-3 lg:row-span-2 p-6 flex flex-col relative overflow-hidden bg-accent-soft-light dark:bg-accent-soft-dark border-2 border-brand-600 dark:border-brand-400 shadow-xl shadow-brand-500/10 hover:shadow-2xl hover:shadow-brand-500/20 transition-all duration-300 hover:-translate-y-1">
                  {/* Glow effect */}
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand-500/20 rounded-full blur-3xl"></div>
                  <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-brand-500/10 rounded-full blur-3xl"></div>

                  {/* Most Popular Badge */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-brand-600 dark:bg-brand-400 text-on-accent-light dark:text-on-accent-dark text-[10px] font-black uppercase tracking-[0.15em] px-4 py-1.5 rounded-b-lg shadow-lg whitespace-nowrap">
                      {t('pricing.mostPopular')}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-4 mt-4">
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                      {planLabel(displayPlans[2])}
                    </h3>
                    <Icons.Crown className="w-6 h-6 text-brand-600 dark:text-brand-400 " />
                  </div>
                  <p className="text-sm text-text-muted-light dark:text-text-muted-dark mb-4">
                    {planDescription(displayPlans[2])}
                  </p>
                  <div className="mb-6">
                    <span className="whitespace-nowrap text-4xl leading-none font-black text-slate-900 dark:text-white">
                      {formatPlanPrice(displayPlans[2].monthlyPrice, displayPlans[2].currency)}
                    </span>
                    <span className="ml-1 whitespace-nowrap text-text-subtle-light dark:text-text-subtle-dark text-sm">
                      {t('pricing.perMonth')}
                    </span>
                  </div>
                  <ul className="space-y-3 mb-6 flex-grow">
                    <li className="flex items-center text-sm text-slate-700 dark:text-slate-300 font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.HardDrive className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 " />
                      </div>
                      {t('pricing.storageAmount', { amount: displayPlans[2].storageIncludedGB })}
                    </li>
                    <li className="flex items-center text-sm text-slate-700 dark:text-slate-300 font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.Cpu className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 " />
                      </div>
                      {t('pricing.agentsIncluded', { count: displayPlans[2].agentsIncluded })}
                    </li>
                    <li className="flex items-center text-sm text-slate-700 dark:text-slate-300 font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.Clock className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 " />
                      </div>
                      {t('pricing.retention', { duration: getRetentionDays(displayPlans[2]) })}
                    </li>
                    <li className="flex items-center text-sm text-slate-700 dark:text-slate-300 font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.Headphones className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 " />
                      </div>
                      {t('pricing.support', { level: getSupportLevel(displayPlans[2]) })}
                    </li>
                    {displayPlans[2].extraAgentPrice && (
                      <li className="flex items-center text-sm text-slate-700 dark:text-slate-300 font-medium">
                        <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                          <Icons.UserPlus className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 " />
                        </div>
                        {t('pricing.extraAgents')}{' '}
                        {formatPlanPrice(displayPlans[2].extraAgentPrice, displayPlans[2].currency)}
                      </li>
                    )}
                  </ul>
                  <Button
                    onClick={() => handleGetStarted(displayPlans[2].name)}
                    variant="primary"
                    className="w-full font-bold py-3 shadow-lg"
                  >
                    {t('pricing.buyNow')}
                  </Button>
                </Card>
              )}

              {/* Business - Premium Card (same height as Professional) */}
              {displayPlans[3] && (
                <Card className="lg:col-span-3 lg:row-span-2 p-6 flex flex-col relative overflow-hidden bg-surface-dark border-2 border-border-strong-dark shadow-xl shadow-surface-dark/40 hover:shadow-2xl hover:shadow-surface-dark/50 transition-all duration-300 hover:-translate-y-1">
                  {/* Glow effect */}
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand-500/10 rounded-full blur-3xl"></div>
                  <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-brand-500/5 rounded-full blur-3xl"></div>

                  {/* Premium Badge */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-surface-card-dark text-white text-[10px] font-black uppercase tracking-[0.15em] px-4 py-1.5 rounded-b-lg shadow-lg whitespace-nowrap border-b border-border-strong-dark">
                      {t('pricing.businessGrade')}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-4 mt-4">
                    <h3 className="text-2xl font-black text-white">{planLabel(displayPlans[3])}</h3>
                    <Icons.Building2 className="w-6 h-6 text-brand-400" />
                  </div>
                  <p className="text-sm text-text-subtle-dark mb-4">
                    {planDescription(displayPlans[3])}
                  </p>
                  <div className="mb-6">
                    <span className="whitespace-nowrap text-4xl leading-none font-black text-white">
                      {formatPlanPrice(displayPlans[3].monthlyPrice, displayPlans[3].currency)}
                    </span>
                    <span className="ml-1 whitespace-nowrap text-text-muted-dark text-sm">
                      {t('pricing.perMonth')}
                    </span>
                  </div>
                  <ul className="space-y-3 mb-6 flex-grow">
                    <li className="flex items-center text-sm text-text-muted-dark font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.HardDrive className="w-3.5 h-3.5 text-brand-400" />
                      </div>
                      {t('pricing.storageAmount', { amount: displayPlans[3].storageIncludedGB })}
                    </li>
                    <li className="flex items-center text-sm text-text-muted-dark font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.Cpu className="w-3.5 h-3.5 text-brand-400" />
                      </div>
                      {t('pricing.agentsIncluded', { count: displayPlans[3].agentsIncluded })}
                    </li>
                    <li className="flex items-center text-sm text-text-muted-dark font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.Infinity className="w-3.5 h-3.5 text-brand-400" />
                      </div>
                      <span className="text-brand-400 font-bold">
                        {t('pricing.unlimitedAgents')}
                      </span>
                    </li>
                    <li className="flex items-center text-sm text-text-muted-dark font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.Clock className="w-3.5 h-3.5 text-brand-400" />
                      </div>
                      {t('pricing.retention', { duration: getRetentionDays(displayPlans[3]) })}
                    </li>
                    <li className="flex items-center text-sm text-text-muted-dark font-medium">
                      <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                        <Icons.Headphones className="w-3.5 h-3.5 text-brand-400" />
                      </div>
                      {t('pricing.prioritySupport')}
                    </li>
                    {displayPlans[3].extraAgentPrice && (
                      <li className="flex items-center text-sm text-text-muted-dark font-medium">
                        <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                          <Icons.UserPlus className="w-3.5 h-3.5 text-brand-400" />
                        </div>
                        {t('pricing.extraAgents')}{' '}
                        {formatPlanPrice(displayPlans[3].extraAgentPrice, displayPlans[3].currency)}
                      </li>
                    )}
                    {displayPlans[3].extraStoragePrice > 0 && (
                      <li className="flex items-center text-sm text-text-muted-dark font-medium">
                        <div className="w-6 h-6 rounded-full bg-brand-500/10 flex items-center justify-center mr-3">
                          <Icons.Database className="w-3.5 h-3.5 text-brand-400" />
                        </div>
                        {t('pricing.extraStoragePrefix')}{' '}
                        {formatPlanPrice(
                          displayPlans[3].extraStoragePrice,
                          displayPlans[3].currency
                        )}
                        /{displayPlans[3].extraStorageAmountGB}GB
                      </li>
                    )}
                  </ul>
                  <Button
                    onClick={() => handleGetStarted(displayPlans[3].name)}
                    variant="primary"
                    className="w-full bg-surface-card-dark hover:bg-surface-2-dark text-white font-bold py-3 shadow-lg border border-border-strong-dark"
                  >
                    {t('pricing.buyNow')}
                  </Button>
                </Card>
              )}

              {/* Custom - Full Featured Bottom */}
              {displayPlans[4] && (
                <Card className="lg:col-span-6 p-6 flex flex-col lg:flex-row gap-6 bg-accent-soft-light dark:bg-accent-soft-dark border-2 border-brand-600/30 dark:border-brand-400/30 hover:shadow-xl hover:shadow-brand-500/10 transition-all duration-300">
                  {/* Left side - Header & Price */}
                  <div className="lg:w-1/3 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-black text-slate-900 dark:text-white">
                        {planLabel(displayPlans[4])}
                      </h3>
                      <Icons.Sparkles className="w-5 h-5 text-brand-600 dark:text-brand-400 " />
                    </div>
                    <p className="text-sm text-text-muted-light dark:text-text-muted-dark mb-4">
                      {planDescription(displayPlans[4])}
                    </p>
                    <div className="mb-4">
                      <span className="text-3xl font-black text-slate-900 dark:text-white">
                        {formatPlanPrice(displayPlans[4].monthlyPrice, displayPlans[4].currency)}
                      </span>
                      <span className="text-text-subtle-light dark:text-text-subtle-dark text-sm">
                        {t('pricing.perMonth')}
                      </span>
                    </div>
                    <Button
                      onClick={() => handleGetStarted(displayPlans[4].name)}
                      variant="primary"
                      className="w-full lg:w-auto font-bold shadow-lg"
                    >
                      {t('pricing.buyNow')}
                    </Button>
                  </div>

                  {/* Right side - Full Features Grid */}
                  <div className="lg:w-2/3 grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-card-light/50 dark:bg-surface-card-dark/50">
                      <div className="w-8 h-8 rounded-full bg-brand-500/10 flex items-center justify-center shrink-0">
                        <Icons.HardDrive className="w-4 h-4 text-brand-600 dark:text-brand-400 " />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {displayPlans[4].storageIncludedGB} GB
                        </p>
                        <p className="text-[10px] text-text-muted-light dark:text-text-muted-dark">
                          {t('pricing.baseStorage')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-card-light/50 dark:bg-surface-card-dark/50">
                      <div className="w-8 h-8 rounded-full bg-brand-500/10 flex items-center justify-center shrink-0">
                        <Icons.Cpu className="w-4 h-4 text-brand-600 dark:text-brand-400 " />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {t('pricing.agents', { count: displayPlans[4].agentsIncluded })}
                        </p>
                        <p className="text-[10px] text-text-muted-light dark:text-text-muted-dark">
                          {t('pricing.included')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-card-light/50 dark:bg-surface-card-dark/50">
                      <div className="w-8 h-8 rounded-full bg-brand-500/10 flex items-center justify-center shrink-0">
                        <Icons.Infinity className="w-4 h-4 text-brand-600 dark:text-brand-400 " />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {t('pricing.unlimited')}
                        </p>
                        <p className="text-[10px] text-text-muted-light dark:text-text-muted-dark">
                          {t('pricing.agentScaling')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-card-light/50 dark:bg-surface-card-dark/50">
                      <div className="w-8 h-8 rounded-full bg-brand-500/10 flex items-center justify-center shrink-0">
                        <Icons.UserPlus className="w-4 h-4 text-brand-600 dark:text-brand-400 " />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {formatPlanPrice(
                            displayPlans[4].extraAgentPrice || 0,
                            displayPlans[4].currency
                          )}
                        </p>
                        <p className="text-[10px] text-text-muted-light dark:text-text-muted-dark">
                          {t('pricing.perExtraAgent')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-card-light/50 dark:bg-surface-card-dark/50">
                      <div className="w-8 h-8 rounded-full bg-brand-500/10 flex items-center justify-center shrink-0">
                        <Icons.Database className="w-4 h-4 text-brand-600 dark:text-brand-400 " />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {formatPlanPrice(
                            displayPlans[4].extraStoragePrice,
                            displayPlans[4].currency
                          )}
                        </p>
                        <p className="text-[10px] text-text-muted-light dark:text-text-muted-dark">
                          {t('pricing.perStorage', {
                            amount: displayPlans[4].extraStorageAmountGB,
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-card-light/50 dark:bg-surface-card-dark/50">
                      <div className="w-8 h-8 rounded-full bg-brand-500/10 flex items-center justify-center shrink-0">
                        <Icons.Headphones className="w-4 h-4 text-brand-600 dark:text-brand-400 " />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {t('pricing.priority')}
                        </p>
                        <p className="text-[10px] text-text-muted-light dark:text-text-muted-dark">
                          {t('pricing.support247')}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Plan Comparison Table - Dynamic */}
        {displayPlans.length > 0 && (
          <div className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-widest">
                {t('pricing.comparison')}
              </h2>
            </div>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark">
                      <th className="px-4 py-4 text-left text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.15em]">
                        {t('pricing.feature')}
                      </th>
                      {displayPlans.map((plan, index) => (
                        <th
                          key={plan.id}
                          className={`px-4 py-4 text-center text-[10px] font-black uppercase tracking-[0.15em] ${
                            index === 2
                              ? 'text-brand-600 dark:text-brand-400'
                              : 'text-text-subtle-light dark:text-text-subtle-dark'
                          }`}
                        >
                          {planLabel(plan)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">{t('pricing.monthlyPrice')}</td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10 font-bold' : ''}`}
                        >
                          {plan.monthlyPrice === 0
                            ? t('pricing.free')
                            : formatPlanPrice(plan.monthlyPrice, plan.currency)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">
                        {t('pricing.agentsIncludedLabel')}
                      </td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10 font-bold' : ''}`}
                        >
                          {plan.agentsIncluded}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">
                        {t('pricing.storageIncluded')}
                      </td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10 font-bold' : ''}`}
                        >
                          {plan.storageIncludedGB} GB
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">{t('pricing.maxAgents')}</td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10' : ''}`}
                        >
                          {formatMaxAgents(plan)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">{t('pricing.extraAgent')}</td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10' : ''}`}
                        >
                          {formatExtraAgentPrice(plan)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">{t('pricing.bonusStorage')}</td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10' : ''}`}
                        >
                          {formatBonusStorage(plan)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">{t('pricing.extraStorage')}</td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10' : ''}`}
                        >
                          {formatExtraStorage(plan)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">
                        {t('pricing.backupHistory')}{' '}
                        <span className="font-normal text-text-subtle-light dark:text-text-subtle-dark">
                          {t('pricing.restoreWindow')}
                        </span>
                      </td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10' : ''}`}
                        >
                          {getRetentionDays(plan)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">
                        {t('pricing.schedulingLabel')}
                      </td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10' : ''}`}
                        >
                          {getScheduling(plan)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold">{t('pricing.supportLabel')}</td>
                      {displayPlans.map((plan, index) => (
                        <td
                          key={plan.id}
                          className={`px-4 py-3 text-xs text-center ${index === 2 ? 'bg-accent-soft-light/10 dark:bg-accent-soft-dark/10' : ''}`}
                        >
                          {getSupportLevel(plan)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-widest">
              {t('pricing.faqTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            <FaqItem q={t('pricing.faq0Question')} a={t('pricing.faq0Answer')} />
            <FaqItem q={t('pricing.faq1Question')} a={t('pricing.faq1Answer')} />
            <FaqItem q={t('pricing.faq2Question')} a={t('pricing.faq2Answer')} />
            <FaqItem q={t('pricing.faq3Question')} a={t('pricing.faq3Answer')} />
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div>
      <h3 className="text-sm font-black text-slate-900 dark:text-white mb-2 uppercase tracking-widest">
        {q}
      </h3>
      <p className="text-sm text-text-muted-light dark:text-text-muted-dark font-medium leading-relaxed">
        {a}
      </p>
    </div>
  );
}

export default Pricing;
